import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useAlert } from 'react-alert';
import Sidebar from '../../components/AdminDashboard/Sidebar';
import TopBar from '../../components/AdminDashboard/TopBar';
import MetaData from '../../layouts/MetaData';
import StaffOrgAssign from '../../components/Form/StaffOrgAssign';
import { RoleBadge } from '../../utils/roleLabels';
import { personnelApi, errMsg, targetOf } from '../../components/Personnel/personnelApi';
import PasswordResetForm from '../../components/Personnel/PasswordResetForm';
import CredentialSection from '../../components/Personnel/CredentialSection';

// Legacy per-collection screens (patients / assignments) reachable from the unified profile.
const legacyLink = (p) => {
  if (p.kind === 'doctor') return { pathname: '/doctorProfile', state: { id: p.id } };
  if (p.kind === 'hr') {
    const [firstname, ...rest] = (p.name || '').split(' ');
    return { pathname: '/hrProfile', state: { hr: { _id: p.id, firstname, lastname: rest.join(' '), email: p.email, role: p.role } } };
  }
  return null;
};

const PersonnelProfile = () => {
  const { kind, id } = useParams();
  const alert = useAlert();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [statusModal, setStatusModal] = useState(false);
  const [reason, setReason] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [roleConfirm, setRoleConfirm] = useState(false);
  const [contact, setContact] = useState({ name: '', email: '', mobileNo: '', phone1: '' });
  const [invitable, setInvitable] = useState([]);
  const [inviteConfirm, setInviteConfirm] = useState(false);
  // Only the response for the profile in the URL may be shown or acted on (no stale or out-of-order profile).
  const routeKey = `${kind}:${id}`;
  const routeRef = useRef(routeKey);
  routeRef.current = routeKey;

  useEffect(() => {
    personnelApi
      .createOptions()
      .then((o) => setInvitable((o.roles || []).map((r) => r.value)))
      .catch(() => setInvitable([]));
  }, []);

  const shown = data && data.person && `${data.person.kind}:${data.person.id}` === routeKey ? data : null;
  const p = shown?.person;

  const resendInvite = async () => {
    if (!p) return;
    setBusy(true);
    try {
      const r = await personnelApi.resendInvite(p.kind, p.id, targetOf(p));
      setInviteConfirm(false);
      if (r.invite?.sent) alert.success('Set-password invite sent');
      else alert.error('Invite email could not be sent');
    } catch (e) {
      alert.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(() => {
    const requested = `${kind}:${id}`;
    personnelApi
      .profile(kind, id)
      .then((d) => {
        if (routeRef.current !== requested || `${d.person.kind}:${d.person.id}` !== requested) return;
        setData(d);
        setRole(d.person.role);
        setContact({ name: d.person.name, email: d.person.email, mobileNo: d.person.mobileNo || '', phone1: d.person.phone1 || '' });
        setError(null);
      })
      .catch((e) => routeRef.current === requested && setError(errMsg(e)));
  }, [kind, id]);

  useEffect(() => {
    setData(null);
    setError(null);
    setStatusModal(false);
    setRoleConfirm(false);
    setInviteConfirm(false);
    load();
  }, [load]);

  const toggleStatus = async () => {
    if (!p) return;
    setBusy(true);
    try {
      await personnelApi.setStatus(p.kind, p.id, { active: !p.active, reason }, targetOf(p));
      alert.success(p.active ? 'Profile deactivated' : 'Profile reactivated');
      setStatusModal(false);
      setReason('');
      load();
    } catch (e) {
      alert.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveRole = async () => {
    if (!p) return;
    setBusy(true);
    try {
      await personnelApi.setRole(p.kind, p.id, role, targetOf(p));
      setRoleConfirm(false);
      alert.success('Portal role updated');
      load();
    } catch (e) {
      alert.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveContact = async (e) => {
    e.preventDefault();
    if (!p) return;
    setBusy(true);
    try {
      await personnelApi.editCoordinator(p.kind, p.id, contact, targetOf(p));
      alert.success('Coordinator updated');
      load();
    } catch (err) {
      alert.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const perms = shown?.permissions || {};
  const orgName = Object.fromEntries((shown?.organizations || []).map((o) => [o.id, o.name]));
  const legacy = p && legacyLink(p);

  return (
    <>
      <MetaData title="Personnel Profile" />
      <Sidebar />
      <section className="home-section">
        <TopBar />
        <div className="shadow-lg p-3 mb-5 mr-4 ml-4 rounded-card">
          <div className="home-content">
            <Link to="/personnel" className="link">&larr; HR Personnel</Link>
            {error && <div className="alert alert-danger mt-2">{error}</div>}
            {!p && !error && <p>Loading...</p>}
            {p && (
              <>
                <div className="d-flex justify-content-between align-items-start mt-2">
                  <div>
                    <h4 style={{ color: '#004aad', marginBottom: 4 }}>{p.name}</h4>
                    <div>
                      <RoleBadge role={p.role} organizationIds={p.organizationIds} label={p.roleLabel} />{' '}
                      <span className="badge bg-light text-dark">{p.sourceType}</span>{' '}
                      {p.active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-danger">Deactivated</span>
                      )}
                    </div>
                    <div className="mt-1">{p.email}</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      Organizations: {p.organizationIds.length ? p.organizationIds.map((o) => orgName[o] || o).join(', ') : 'Global (all)'}
                    </div>
                  </div>
                  <div className="text-end">
                    {!perms.canManage && !perms.canEditCoordinator && <span className="badge bg-secondary">Read-only</span>}
                    {legacy && (
                      <div className="mt-1">
                        <Link to={legacy} className="link">Patients & assignments &rarr;</Link>
                      </div>
                    )}
                  </div>
                </div>

                {p.duplicateEmail && (
                  <div className="alert alert-warning mt-2 mb-0 py-2">
                    This email is also used by:{' '}
                    {p.duplicates.map((d, i) => (
                      <span key={`${d.kind}:${d.id}`}>
                        {i > 0 && ', '}
                        <Link to={`/personnel/${d.kind}/${d.id}`}>{d.sourceType} account ({d.roleLabel})</Link>
                      </span>
                    ))}
                    . Accounts are kept separate.
                  </div>
                )}

                {perms.canEditCoordinator && <form className="p-3 mt-3" onSubmit={saveContact} style={{ border: '1px solid #dee2e6' }}>
                  <h6>Coordinator name and contact</h6>
                  <div className="row">{[
                    ['name', 'Name', 'text'], ['email', 'Email', 'email'], ['mobileNo', 'Mobile', 'tel'], ['phone1', 'Phone', 'tel'],
                  ].map(([field, label, type]) => <label className="col-md-6" key={field}>{label}
                    <input type={type} className="form-control" value={contact[field]} required={field === 'name' || field === 'email'}
                      onChange={(e) => setContact((v) => ({ ...v, [field]: e.target.value }))} />
                  </label>)}</div>
                  <button type="submit" className="edit-pill mt-2" disabled={busy}>Save contact</button>
                </form>}

                {(perms.canManage || perms.canEditCoordinator) && (
                  <>
                    {perms.canAssignOrgs && <StaffOrgAssign kind={p.kind} staffId={p.id} staffRole={p.role} organizationIds={p.organizationIds || []} onSaved={load} />}

                    <div className="p-3 mt-3 d-flex flex-wrap align-items-end" style={{ border: '1px solid #dee2e6', borderRadius: 6, gap: 24 }}>
                      <div>
                        <b>Account status</b>
                        <div>
                          <button
                            type="button"
                            className="edit-pill"
                            style={p.active ? { background: '#b91c1c', color: '#fff' } : undefined}
                            disabled={!perms.canChangeStatus}
                            title={perms.canChangeStatus ? '' : 'You cannot deactivate your own profile'}
                            onClick={() => setStatusModal(true)}
                          >
                            {p.active ? 'Deactivate Profile' : 'Reactivate Profile'}
                          </button>
                        </div>
                      </div>
                      {perms.canChangeRole && (
                        <div>
                          <b>Portal role</b>{!p.usedForLogin && <small className="text-danger d-block">Another record with this email is used at login; changing this one does not change that login.</small>}
                          <div className="d-flex">
                            <select className="form-control" value={role} onChange={(e) => setRole(e.target.value)}>
                              {shown.roleOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            <button type="button" className="edit-pill ms-2" disabled={busy || role === p.role} onClick={() => setRoleConfirm(true)}>
                              Save
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {perms.canResetPassword && <PasswordResetForm key={`${p.kind}:${p.id}`} person={p} />}
                  </>
                )}

                {invitable.includes(p.role) && (
                  <>
                    <div className="p-3 mt-3" style={{ border: '1px solid #dee2e6', borderRadius: 6 }}>
                      <b>Set-password invite</b>
                      <div className="text-muted" style={{ fontSize: 13 }}>Emails this user a new link to set their password.</div>
                      <button type="button" className="edit-pill mt-1" disabled={busy || !p.active} onClick={() => setInviteConfirm(true)}>
                        Resend invite
                      </button>
                    </div>
                  </>
                )}

                <CredentialSection
                  person={p}
                  credentials={shown.credentials || []}
                  organizations={shown.organizations || []}
                  permissions={perms}
                  onChanged={load}
                />
              </>
            )}
          </div>
        </div>
      </section>

      <Modal show={!!p && inviteConfirm} onHide={() => setInviteConfirm(false)} centered>
        <Modal.Body data-testid="confirm-invite">
          <h5>
            Send a set-password invite to {p?.name} ({p?.email})?
          </h5>
          <p className="text-muted mb-0">A new set-password link is emailed to this address only. Recorded in the audit log.</p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setInviteConfirm(false)}>Cancel</button>
          <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={resendInvite}>Send invite</button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!p && roleConfirm} onHide={() => setRoleConfirm(false)} centered>
        <Modal.Body data-testid="confirm-role">
          <h5>
            Change portal role for {p?.name} ({p?.email})?
          </h5>
          <p>
            <b>{p?.roleLabel}</b> &rarr;{' '}
            <b>{(shown?.roleOptions || []).find((o) => o.value === role)?.label || role}</b>
          </p>
          <p className="text-muted mb-0">This changes what this login account can access. The change is recorded in the audit log.</p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRoleConfirm(false)}>Cancel</button>
          <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={saveRole}>Confirm change</button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!p && statusModal} onHide={() => setStatusModal(false)} size="sm" centered>
        <Modal.Body data-testid="confirm-status">
          <b>
            {p?.active ? 'Deactivate this profile' : 'Reactivate this profile'} for {p?.name} ({p?.email})?
          </b>
          <p className="mb-1" style={{ fontSize: 13 }}>
            {p?.active ? 'The person will no longer be able to log in.' : 'The person will be able to log in again.'}
          </p>
          <textarea className="form-control" rows={2} placeholder="Reason (optional, audit-logged)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="mt-2">
            <button type="button" className="edit-pill" disabled={busy} onClick={toggleStatus}>Confirm</button>{' '}
            <button type="button" className="btn btn-link btn-sm" onClick={() => setStatusModal(false)}>Cancel</button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PersonnelProfile;
